import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { mainApiService } from '../services/apiService';
import { endpoints } from '../config/endpoints';
import { getServerConfig } from '../config/serverConfig';
import StripeCardForm, { StripeCardFormHandle } from './StripeCardForm';
import { calculateProratedCents } from '../services/billingUtils';
import { config } from '../config/environment';

// ── Types ─────────────────────────────────────────────────────────────────────

interface BillingSummary {
  billing_month: string;
  current_tier: string;
  pending_tier: string | null;
  status: string;
  cancel_at: string | null;
  session_count: number;
  projected_tier: string;
  projected_price_cents: number;
  coupon: { code: string; discount_pct: number; duration_months: number | null } | null;
  tiers: { name: string; price_cents: number; min_sessions: number }[];
}

interface TrialStatus {
  in_trial: boolean;
  days_remaining: number;
  trial_ends_at: string | null;
}

interface CouponResult {
  valid: boolean;
  coupon_type: string;
  description: string;
  base_price_cents: number;
  discount_amount_cents: number;
  final_price_cents: number;
  free_months: number | null;
}

interface MonthCount {
  month: string;
  billable_count: number;
  total_count: number;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const TIERS = [
  {
    id: 'starter',
    name: 'Starter',
    price: 24.99,
    priceCents: 2499,
    description: 'For new or part-time practices',
    features: ['Up to 10 sessions/month', '1 GB storage', 'All features included'],
  },
  {
    id: 'growth',
    name: 'Growth',
    price: 39.99,
    priceCents: 3999,
    description: 'For active practices',
    features: ['Up to 30 sessions/month', '2 GB storage', 'All features included'],
    highlighted: true,
  },
  {
    id: 'elite',
    name: 'Elite',
    price: 69.99,
    priceCents: 6999,
    description: 'For high-volume practices',
    features: ['Unlimited sessions', '3 GB storage', 'All features included'],
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function currentMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

function prevMonth(yyyymm: string): string {
  const [y, m] = yyyymm.split('-').map(Number);
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
}

function monthLabel(yyyymm: string): string {
  const [y, m] = yyyymm.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}


// ── Types ─────────────────────────────────────────────────────────────────────

interface PaymentMethodInfo {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

// ── PaymentMethodTab ──────────────────────────────────────────────────────────

function PaymentMethodTab({ billingActive }: { billingActive: boolean }) {
  const [pm, setPm] = useState<PaymentMethodInfo | null>(null);
  const [loadingPm, setLoadingPm] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [pmError, setPmError] = useState('');

  useEffect(() => {
    if (!billingActive) return;
    setLoadingPm(true);
    mainApiService.get<{ payment_method: PaymentMethodInfo | null }>(endpoints.main.billing.paymentMethod)
      .then(res => {
        if (res.success && res.data) setPm(res.data.payment_method);
      })
      .catch(() => {})
      .finally(() => setLoadingPm(false));
  }, [billingActive]);

  const handleManage = async () => {
    setRedirecting(true);
    setPmError('');
    try {
      const res = await mainApiService.post<{ url: string }>(endpoints.main.billing.portalSession, {});
      if (res.success && res.data?.url) {
        window.location.href = res.data.url;
      } else {
        setPmError(res.error || 'Could not open payment portal');
        setRedirecting(false);
      }
    } catch {
      setPmError('Could not open payment portal. Please try again.');
      setRedirecting(false);
    }
  };

  if (!billingActive) {
    return (
      <div className="bg-dark-card border border-dark-border rounded-xl p-6 text-center">
        <p className="text-sm text-gray-400">Payment method management is not available yet.</p>
        <p className="text-xs text-gray-600 mt-1">Billing is currently in beta mode.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-dark-card border border-dark-border rounded-xl p-6">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-4">Payment Method on File</p>
        {loadingPm ? (
          <p className="text-sm text-gray-500">Loading&hellip;</p>
        ) : pm ? (
          <div className="flex items-center gap-4">
            <div className="bg-dark-hover rounded-lg px-4 py-3 flex items-center gap-3">
              <span className="text-white font-semibold capitalize">{pm.brand}</span>
              <span className="text-gray-400 text-sm">ending in {pm.last4}</span>
              <span className="text-gray-600 text-xs">exp {pm.exp_month}/{pm.exp_year}</span>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No payment method on file.</p>
        )}
      </div>

      <button
        onClick={handleManage}
        disabled={redirecting}
        className="px-5 py-2.5 bg-violet-700 hover:bg-violet-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50 transition-colors"
      >
        {redirecting ? 'Opening portal...' : pm ? 'Update Payment Method' : 'Add Payment Method'}
      </button>

      {pmError && <p className="text-xs text-red-400">{pmError}</p>}

      <p className="text-xs text-gray-600">
        You will be redirected to a secure Stripe-hosted page to manage your payment method.
      </p>
    </div>
  );
}

// ── PaymentHistoryTab ─────────────────────────────────────────────────────────

interface InvoiceRow {
  id: string;
  billing_month: string;
  tier: string;
  session_count: number;
  net_amount_cents: number;
  discount_pct: number;
  status: string;
}

function statusBadge(status: string) {
  const map: Record<string, string> = {
    paid:         'bg-green-900/40 text-green-400 border-green-800/50',
    issued:       'bg-yellow-900/30 text-yellow-400 border-yellow-800/40',
    failed:       'bg-red-900/30 text-red-400 border-red-800/40',
    beta_period:  'bg-gray-800/60 text-gray-500 border-gray-700/50',
    void:         'bg-gray-800/60 text-gray-500 border-gray-700/50',
  };
  return map[status] ?? map.void;
}

function PaymentHistoryTab() {
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    mainApiService.get<{ invoices: InvoiceRow[] }>(endpoints.main.billing.invoices)
      .then(res => { if (res.success && res.data) setInvoices(res.data.invoices ?? []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const downloadPdf = async (inv: InvoiceRow) => {
    try {
      const { config } = await import('../config/environment');
      const token = localStorage.getItem('authToken') || '';
      const res = await fetch(`${config.apiBaseUrl}/billing/invoices/${inv.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CoachSight-Invoice-${inv.billing_month}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* non-fatal */ }
  };

  if (loading) return <p className="text-sm text-gray-500">Loading&hellip;</p>;
  if (!invoices.length) return <p className="text-sm text-gray-500">No invoices yet.</p>;

  return (
    <div className="bg-dark-card border border-dark-border rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-dark-hover border-b border-dark-border text-xs">
          <tr>
            <th className="text-left px-4 py-2.5 text-gray-500 font-medium">Month</th>
            <th className="text-left px-4 py-2.5 text-gray-500 font-medium">Plan</th>
            <th className="text-right px-4 py-2.5 text-gray-500 font-medium">Sessions</th>
            <th className="text-right px-4 py-2.5 text-gray-500 font-medium">Amount</th>
            <th className="text-right px-4 py-2.5 text-gray-500 font-medium">Status</th>
            <th className="px-4 py-2.5" />
          </tr>
        </thead>
        <tbody className="divide-y divide-dark-border">
          {invoices.map(inv => (
            <tr key={inv.id} className="hover:bg-dark-hover/40 transition-colors">
              <td className="px-4 py-2.5 text-gray-300 whitespace-nowrap">{monthLabel(inv.billing_month)}</td>
              <td className="px-4 py-2.5 capitalize text-gray-400">{inv.tier}</td>
              <td className="px-4 py-2.5 text-right text-gray-400">{inv.session_count}</td>
              <td className="px-4 py-2.5 text-right text-gray-300 whitespace-nowrap">
                {formatCents(inv.net_amount_cents)}
                {inv.discount_pct > 0 && <span className="ml-1 text-green-500">-{inv.discount_pct}%</span>}
              </td>
              <td className="px-4 py-2.5 text-right">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-medium capitalize ${statusBadge(inv.status)}`}>
                  {inv.status === 'beta_period' ? 'beta' : inv.status}
                </span>
              </td>
              <td className="px-4 py-2.5 text-right">
                <button
                  onClick={() => downloadPdf(inv)}
                  className="text-[10px] text-primary-400 hover:text-primary-300 transition-colors"
                >
                  PDF
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function BillingTab() {
  const [activeTab, setActiveTab] = useState<'overview' | 'payment' | 'history'>('overview');
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [trialStatus, setTrialStatus] = useState<TrialStatus | null>(null);
  const [monthCounts, setMonthCounts] = useState<MonthCount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Upgrade form state
  const [billingActive, setBillingActive] = useState(false);
  const [stripeKey, setStripeKey] = useState('');
  const [selectedTier, setSelectedTier] = useState('growth');
  const [couponCode, setCouponCode] = useState('');
  const [couponResult, setCouponResult] = useState<CouponResult | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [agreeToPolicy, setAgreeToPolicy] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeError, setSubscribeError] = useState<string | null>(null);
  const stripeRef = useRef<StripeCardFormHandle>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const sumRes = await mainApiService.get<BillingSummary>(endpoints.main.billing.summary);
      if (sumRes.success && sumRes.data) setSummary(sumRes.data);

      // Fetch last 3 months of session counts
      const months = [currentMonth(), prevMonth(currentMonth()), prevMonth(prevMonth(currentMonth()))];
      const counts: MonthCount[] = await Promise.all(
        months.map(async m => {
          const r = await mainApiService.get<{ billable_count: number; total_count: number }>(
            endpoints.main.billing.sessions(m)
          );
          return {
            month: m,
            billable_count: r.success && r.data ? r.data.billable_count : 0,
            total_count: r.success && r.data ? r.data.total_count : 0,
          };
        })
      );
      setMonthCounts(counts);
    } catch {
      setError('Failed to load billing data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    getServerConfig().then(cfg => {
      setBillingActive(cfg.billing_charging_active);
      setStripeKey(cfg.stripe_publishable_key);
      if (cfg.billing_charging_active) {
        mainApiService.get<TrialStatus>(endpoints.main.billing.trialStatus)
          .then(res => { if (res.success && res.data) setTrialStatus(res.data); })
          .catch(() => {});
      }
    });
  }, [load]);

  // Reset coupon when tier changes
  useEffect(() => {
    setCouponResult(null);
    setCouponError(null);
  }, [selectedTier]);

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) return;
    setValidatingCoupon(true);
    setCouponError(null);
    setCouponResult(null);
    try {
      const res = await mainApiService.post<CouponResult>(endpoints.main.billing.validateCoupon, {
        code: couponCode.trim(),
        tier: selectedTier,
      });
      if (res.success && res.data) setCouponResult(res.data);
      else setCouponError(res.error || 'Invalid coupon code');
    } catch {
      setCouponError('Invalid coupon code');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleSubscribe = async () => {
    if (billingActive && !agreeToPolicy) {
      setSubscribeError('Please accept the subscription policy to continue.');
      return;
    }
    setSubscribing(true);
    setSubscribeError(null);
    try {
      if (billingActive) {
        const tok = localStorage.getItem('authToken') || '';
        const siRes = await fetch(`${config.apiBaseUrl}/billing/setup-intent`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${tok}` },
        });
        if (!siRes.ok) throw new Error('Failed to initialize payment');
        const { client_secret } = await siRes.json();
        if (!stripeRef.current) throw new Error('Card form not ready');
        const confirmResult = await stripeRef.current.confirmSetup(client_secret);
        if ('error' in confirmResult) throw new Error(confirmResult.error);
        const pmRes = await fetch(`${config.apiBaseUrl}/billing/set-default-payment-method`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${tok}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ payment_method_id: confirmResult.paymentMethodId }),
        });
        if (!pmRes.ok) throw new Error('Failed to save payment method');
      }
      const res = await mainApiService.post(endpoints.main.billing.subscribe, {
        tier: selectedTier,
        coupon_code: couponCode.trim() || null,
      });
      if (!res.success) throw new Error(res.error || 'Subscription failed');
      await load();
      setTrialStatus(null);
    } catch (err: any) {
      setSubscribeError(err?.message || 'Subscription failed. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  // Plan change state (shown when subscribed)
  const [planTarget, setPlanTarget] = useState<string | null>(null);
  const [planAction, setPlanAction] = useState<'upgrade' | 'downgrade' | null>(null);
  const [agreeToPlanChange, setAgreeToPlanChange] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);
  const [planError, setPlanError] = useState('');
  const [planSuccess, setPlanSuccess] = useState('');
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  const TIER_ORDER: Record<string, number> = { starter: 1, growth: 2, elite: 3 };

  const handleSelectTier = (tier: string) => {
    if (!summary || tier === summary.current_tier) return;
    setPlanError('');
    setPlanSuccess('');
    setAgreeToPlanChange(false);
    setPlanTarget(tier);
    setPlanAction(TIER_ORDER[tier] > TIER_ORDER[summary.current_tier] ? 'upgrade' : 'downgrade');
  };

  const handleConfirmPlanChange = async () => {
    if (!planTarget || !planAction) return;
    setChangingPlan(true);
    setPlanError('');
    const endpoint = planAction === 'upgrade' ? endpoints.main.billing.upgrade : endpoints.main.billing.downgrade;
    const res = await mainApiService.post(endpoint, { tier: planTarget });
    if (res.success) {
      setPlanSuccess(planAction === 'upgrade'
        ? `Upgraded to ${planTarget}. Effective immediately.`
        : `Downgrade to ${planTarget} scheduled for your next billing cycle.`);
      setPlanTarget(null);
      setPlanAction(null);
      await load();
    } else {
      setPlanError(res.error || 'Could not change plan. Please try again.');
    }
    setChangingPlan(false);
  };

  const handleCancel = async () => {
    setCancelling(true);
    setPlanError('');
    const res = await mainApiService.post(endpoints.main.billing.cancel, {});
    if (res.success) {
      setCancelConfirm(false);
      await load();
    } else {
      setPlanError(res.error || 'Could not cancel subscription.');
    }
    setCancelling(false);
  };

  const handleReactivate = async () => {
    setReactivating(true);
    const res = await mainApiService.post(endpoints.main.billing.reactivate, {});
    if (res.success) await load();
    setReactivating(false);
  };

  const selectedTierData = TIERS.find(t => t.id === selectedTier)!;
  const effectivePrice = couponResult
    ? couponResult.coupon_type === 'free_months' ? selectedTierData.priceCents : couponResult.final_price_cents
    : selectedTierData.priceCents;
  const proratedCents = calculateProratedCents(effectivePrice, new Date());
  const showUpgrade = billingActive && trialStatus?.in_trial;

  // Recommend a tier based on avg billable sessions over last 2 months
  const recommendedTier = useMemo(() => {
    const recent = monthCounts.slice(0, 2).filter(mc => mc.billable_count > 0 || mc.total_count > 0);
    if (!recent.length) return null;
    const avg = recent.reduce((s, mc) => s + mc.billable_count, 0) / recent.length;
    if (avg >= 31) return 'elite';
    if (avg >= 11) return 'growth';
    return 'starter';
  }, [monthCounts]);

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-white">Billing</h2>

      {/* Sub-tabs */}
      <div className="flex gap-1 bg-dark-card border border-dark-border rounded-xl p-1 w-fit">
        {([
          { key: 'overview', label: 'Subscription' },
          { key: 'payment',  label: 'Payment Method' },
          { key: 'history',  label: 'Payment History' },
        ] as const).map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === key
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-gray-400 hover:text-gray-200 hover:bg-dark-hover'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'payment' && <PaymentMethodTab billingActive={billingActive} />}
      {activeTab === 'history' && <PaymentHistoryTab />}

      {activeTab === 'overview' && <>
      {error && <p className="text-sm text-red-400">{error}</p>}

      {/* Trial status banner */}
      {billingActive && trialStatus?.in_trial && (
        <div className="bg-indigo-950/40 border border-indigo-800 rounded-xl p-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-indigo-300">Free Trial Active</p>
            <p className="text-xs text-indigo-400 mt-0.5">
              {trialStatus.days_remaining <= 1
                ? 'Your trial ends today. Subscribe below to keep access.'
                : `${trialStatus.days_remaining} day${trialStatus.days_remaining !== 1 ? 's' : ''} remaining in your free trial.`}
              {trialStatus.trial_ends_at && (
                <span className="ml-2 text-indigo-500">
                  (ends {new Date(trialStatus.trial_ends_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })})
                </span>
              )}
            </p>
          </div>
          <span className="text-xs text-indigo-400 bg-indigo-900/50 border border-indigo-800 px-2 py-1 rounded-full whitespace-nowrap">In Trial</span>
        </div>
      )}

      {/* Upgrade section — shown when in trial */}
      {showUpgrade && (
        <div className="bg-dark-card border border-dark-border rounded-xl p-6 space-y-5">
          <p className="text-sm font-semibold text-white">Choose your plan</p>

          {/* Tier grid */}
          <div className="grid grid-cols-3 gap-3">
            {TIERS.map(tier => {
              const isSelected = selectedTier === tier.id;
              return (
                <button
                  key={tier.id}
                  onClick={() => setSelectedTier(tier.id)}
                  style={{
                    border: isSelected ? '2px solid #6d28d9' : '2px solid #374151',
                    borderRadius: '10px', padding: '14px', background: isSelected ? '#1e1440' : 'transparent',
                    cursor: 'pointer', textAlign: 'left', position: 'relative',
                  }}
                >
                  {tier.highlighted && (
                    <span style={{
                      position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)',
                      background: '#6d28d9', color: '#fff', fontSize: '11px', fontWeight: 600,
                      padding: '2px 10px', borderRadius: '99px',
                    }}>Most popular</span>
                  )}
                  <div style={{ fontWeight: 700, fontSize: '15px', color: '#fff', marginBottom: '4px' }}>{tier.name}</div>
                  <div style={{ fontSize: '20px', fontWeight: 800, color: '#a78bfa', marginBottom: '4px' }}>
                    ${tier.price}<span style={{ fontSize: '12px', fontWeight: 400, color: '#9ca3af' }}>/mo</span>
                  </div>
                  <ul style={{ margin: 0, padding: '0 0 0 14px', fontSize: '11px', color: '#9ca3af' }}>
                    {tier.features.map(f => <li key={f} style={{ marginBottom: '2px' }}>{f}</li>)}
                  </ul>
                </button>
              );
            })}
          </div>

          {/* Coupon */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1.5">Coupon code (optional)</label>
            <div className="flex gap-2">
              <input
                value={couponCode}
                onChange={e => { setCouponCode(e.target.value); setCouponResult(null); setCouponError(null); }}
                onKeyDown={e => e.key === 'Enter' && handleValidateCoupon()}
                placeholder="Enter code"
                className="flex-1 px-3 py-2 bg-dark-hover border border-dark-border rounded-lg text-sm text-white placeholder-gray-600"
              />
              <button
                onClick={handleValidateCoupon}
                disabled={!couponCode.trim() || validatingCoupon}
                className="px-4 py-2 bg-primary-700 text-white rounded-lg text-sm disabled:opacity-40"
              >
                {validatingCoupon ? 'Checking...' : 'Apply'}
              </button>
            </div>
            {couponError && <p className="text-xs text-red-400 mt-1">{couponError}</p>}
            {couponResult && (
              <p className="text-xs text-green-400 mt-1 font-medium">
                {couponResult.description}
                {couponResult.coupon_type !== 'free_months' && couponResult.discount_amount_cents > 0 &&
                  ` — ${formatCents(couponResult.final_price_cents)}/mo after discount`}
              </p>
            )}
          </div>

          {/* Stripe card */}
          {billingActive && (
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1.5">Credit Card</label>
              <div className="border border-dark-border rounded-lg p-3 bg-[#1f2937]">
                <StripeCardForm ref={stripeRef} publishableKey={stripeKey} disabled={subscribing} />
              </div>
            </div>
          )}

          {/* Billing disclosure */}
          <p className="text-xs text-gray-600 leading-relaxed">
            You are billed monthly based on your billable session count (sessions 15 min or more; sessions under 15 min, no-shows, and cancellations excluded). Sessions are never cut off. Exceeding your plan's session limit for two consecutive months will automatically move you to the next tier at the higher rate. You can upgrade, downgrade, or cancel anytime.{' '}
            <a href="/terms#13-subscriptions-fees-and-taxes" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-400">Terms of Use</a>
          </p>

          {/* Policy checkbox */}
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={agreeToPolicy}
              onChange={e => setAgreeToPolicy(e.target.checked)}
              className="mt-0.5 accent-violet-600"
            />
            <span className="text-xs text-gray-400 leading-relaxed">
              I understand and agree to the billing terms above.
            </span>
          </label>

          {/* Price summary + subscribe */}
          <div className="flex items-center justify-between bg-dark-hover rounded-lg px-4 py-3">
            <div>
              <p className="text-xs text-gray-500">{selectedTierData.name} plan</p>
              <p className="text-lg font-bold text-white">
                {couponResult?.coupon_type === 'free_months'
                  ? `${formatCents(0)}/mo for first ${couponResult.free_months} month${(couponResult.free_months ?? 1) !== 1 ? 's' : ''}`
                  : `${formatCents(effectivePrice)}/mo`}
              </p>
              {proratedCents < effectivePrice && couponResult?.coupon_type !== 'free_months' && (
                <p className="text-xs text-gray-500">First charge: {formatCents(proratedCents)} (prorated)</p>
              )}
            </div>
            <button
              onClick={handleSubscribe}
              disabled={subscribing}
              className="px-6 py-2.5 bg-violet-700 hover:bg-violet-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
            >
              {subscribing ? 'Activating...' : 'Start Subscription'}
            </button>
          </div>

          {subscribeError && <p className="text-xs text-red-400">{subscribeError}</p>}
        </div>
      )}

      {/* Current plan — shown when subscribed */}
      {!showUpgrade && summary && (
        <div className="space-y-4">
          {/* Status notices */}
          {summary.status === 'cancel_pending' && summary.cancel_at && (
            <div className="bg-red-950/30 border border-red-800/50 rounded-xl p-4 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-red-300">Cancellation Scheduled</p>
                <p className="text-xs text-red-400 mt-0.5">
                  Your subscription ends on {new Date(summary.cancel_at + 'T00:00:00').toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}.
                  You will retain full access until then.
                </p>
              </div>
              <button
                onClick={handleReactivate}
                disabled={reactivating}
                className="px-4 py-2 bg-dark-hover border border-dark-border text-white rounded-lg text-xs font-medium hover:border-primary-700 transition-colors whitespace-nowrap disabled:opacity-50"
              >
                {reactivating ? 'Reactivating...' : 'Keep Subscription'}
              </button>
            </div>
          )}
          {summary.pending_tier && (
            <div className="bg-amber-950/30 border border-amber-800/50 rounded-xl p-3">
              <p className="text-xs text-amber-300">
                Downgrade to <span className="font-semibold capitalize">{summary.pending_tier}</span> is scheduled for your next billing cycle.
              </p>
            </div>
          )}

          {/* Plan cards */}
          <div className="bg-dark-card border border-dark-border rounded-xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-white">Your Plan</p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{summary.session_count} billable sessions this month</span>
                {summary.coupon && (
                  <span className="text-xs text-green-400 bg-green-900/20 border border-green-800/30 px-2 py-0.5 rounded-full">
                    {summary.coupon.discount_pct}% off — {summary.coupon.code}
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              {TIERS.map(tier => {
                const isCurrent = summary.current_tier === tier.id;
                const isTarget = planTarget === tier.id;
                const isRecommended = recommendedTier === tier.id && !isCurrent;
                return (
                  <button
                    key={tier.id}
                    onClick={() => handleSelectTier(tier.id)}
                    disabled={isCurrent}
                    style={{
                      border: isCurrent ? '2px solid #6d28d9' : isTarget ? '2px solid #a78bfa' : isRecommended ? '2px solid #059669' : '2px solid #374151',
                      borderRadius: '10px', padding: '14px',
                      background: isCurrent ? '#1e1440' : isTarget ? '#1e1a30' : isRecommended ? '#052e1c' : 'transparent',
                      cursor: isCurrent ? 'default' : 'pointer', textAlign: 'left', position: 'relative',
                    }}
                  >
                    {isCurrent && (
                      <span style={{
                        position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)',
                        background: '#6d28d9', color: '#fff', fontSize: '10px', fontWeight: 600,
                        padding: '2px 10px', borderRadius: '99px', whiteSpace: 'nowrap',
                      }}>Current plan</span>
                    )}
                    {isRecommended && (
                      <span style={{
                        position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)',
                        background: '#059669', color: '#fff', fontSize: '10px', fontWeight: 600,
                        padding: '2px 10px', borderRadius: '99px', whiteSpace: 'nowrap',
                      }}>Recommended</span>
                    )}
                    <div style={{ fontWeight: 700, fontSize: '14px', color: '#fff', marginBottom: '3px' }}>{tier.name}</div>
                    <div style={{ fontSize: '18px', fontWeight: 800, color: '#a78bfa', marginBottom: '4px' }}>
                      ${tier.price}<span style={{ fontSize: '11px', fontWeight: 400, color: '#9ca3af' }}>/mo</span>
                    </div>
                    <ul style={{ margin: 0, padding: '0 0 0 12px', fontSize: '11px', color: '#9ca3af' }}>
                      {tier.features.map(f => <li key={f} style={{ marginBottom: '2px' }}>{f}</li>)}
                    </ul>
                  </button>
                );
              })}
            </div>
            {recommendedTier && recommendedTier !== summary.current_tier && (
              <p className="text-xs text-gray-500">
                Recommended based on your avg{' '}
                <span className="text-gray-300 font-medium">
                  {Math.round(monthCounts.slice(0, 2).reduce((s, mc) => s + mc.billable_count, 0) / Math.max(1, Math.min(2, monthCounts.slice(0, 2).filter(mc => mc.total_count > 0).length)))} sessions/month
                </span>{' '}
                over the last {Math.min(2, monthCounts.slice(0, 2).filter(mc => mc.total_count > 0).length)} month{monthCounts.slice(0, 2).filter(mc => mc.total_count > 0).length !== 1 ? 's' : ''}.
              </p>
            )}

            {/* Confirm plan change */}
            {planTarget && planAction && (
              <div className="bg-dark-hover border border-dark-border rounded-lg px-4 py-3 space-y-3">
                <p className="text-sm text-gray-300">
                  {planAction === 'upgrade'
                    ? <><span className="text-white font-medium">Upgrade to {TIERS.find(t => t.id === planTarget)?.name}</span> — takes effect immediately.</>
                    : <><span className="text-white font-medium">Downgrade to {TIERS.find(t => t.id === planTarget)?.name}</span> — takes effect at your next billing cycle.</>}
                </p>
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreeToPlanChange}
                    onChange={e => setAgreeToPlanChange(e.target.checked)}
                    className="mt-0.5 accent-violet-600"
                  />
                  <span className="text-xs text-gray-400 leading-relaxed">
                    I understand and agree to the billing terms above.
                  </span>
                </label>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => { setPlanTarget(null); setPlanAction(null); setPlanError(''); setAgreeToPlanChange(false); }}
                    className="px-3 py-1.5 text-xs text-gray-400 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmPlanChange}
                    disabled={changingPlan || !agreeToPlanChange}
                    className={`px-4 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50 transition-colors ${
                      planAction === 'upgrade' ? 'bg-violet-700 hover:bg-violet-600' : 'bg-amber-700 hover:bg-amber-600'
                    }`}
                  >
                    {changingPlan ? 'Saving...' : 'Confirm'}
                  </button>
                </div>
              </div>
            )}

            {planSuccess && <p className="text-xs text-green-400">{planSuccess}</p>}
            {planError && <p className="text-xs text-red-400">{planError}</p>}
          </div>

          {/* Billing disclosure */}
          <p className="text-xs text-gray-600 leading-relaxed">
            You are billed monthly based on your billable session count (sessions 15 min or more; sessions under 15 min, no-shows, and cancellations excluded). Sessions are never cut off. Exceeding your plan's session limit for two consecutive months will automatically move you to the next tier at the higher rate. You can upgrade, downgrade, or cancel anytime.{' '}
            <a href="/terms#13-subscriptions-fees-and-taxes" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-400">Terms of Use</a>
          </p>

          {/* Cancel */}
          {summary.status !== 'cancel_pending' && (
            <div className="text-right">
              {!cancelConfirm ? (
                <button
                  onClick={() => setCancelConfirm(true)}
                  className="text-xs text-gray-600 hover:text-red-400 transition-colors"
                >
                  Cancel subscription
                </button>
              ) : (
                <div className="inline-flex items-center gap-3 bg-red-950/20 border border-red-800/40 rounded-lg px-4 py-2.5">
                  <p className="text-xs text-red-300">Cancel at end of billing period?</p>
                  <button
                    onClick={() => setCancelConfirm(false)}
                    className="text-xs text-gray-400 hover:text-white transition-colors"
                  >
                    Keep it
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={cancelling}
                    className="text-xs bg-red-700 hover:bg-red-600 text-white px-3 py-1 rounded-lg font-medium disabled:opacity-50 transition-colors"
                  >
                    {cancelling ? 'Cancelling...' : 'Yes, cancel'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Session count history — last 3 months */}
      {!loading && monthCounts.length > 0 && (
        <div className="bg-dark-card border border-dark-border rounded-xl p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">Recent Session Activity</p>
          <div className="space-y-2">
            {monthCounts.map(mc => (
              <div key={mc.month} className="flex items-center justify-between text-sm">
                <span className="text-gray-400">{monthLabel(mc.month)}</span>
                <span className="text-white font-medium">
                  {mc.billable_count} billable
                  <span className="text-gray-600 font-normal ml-1">/ {mc.total_count} total</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && <p className="text-sm text-gray-500">Loading&hellip;</p>}
      </>}
    </div>
  );
}
